<template>
  <div>
    <!-- Loader displayed when isLoading is true -->
    <div
      v-if="isLoading"
      class="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50"
    >
      <svg
        class="animate-spin h-16 w-16 text-white"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <circle
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          stroke-width="4"
          fill="none"
        />
      </svg>
    </div>

    <!-- Your page content goes here -->
    <div v-else class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
      <table
        id="patients"
        class="w-full z-10 text-left rtl:text-right text-gray-700 display"
      >
        <thead>
          <tr class="bg-gray-100">
            <th scope="col" class="px-6 py-3">
              <div class="flex items-center">#</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Patient Name</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Patient Unique Code</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Visit Type</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Contact</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Age</div>
            </th>
            <th scope="col" class="px-6">
              <div class="flex items-center">Registered At</div>
            </th>
            <th scope="col" class="px-6">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(patient, key) in patients" :key="key" class="border-b">
            <td class="px-6 py-2">{{ key + 1 }}</td>
            <td class="px-6 py-2">{{ patient.FullName }}</td>
            <td class="px-6 py-2">{{ patient.unique_code }}</td>
            <td class="px-6 py-2">{{ patient.VisitType }}</td>
            <td class="px-6 py-2">{{ patient.PhoneNumber }}</td>
            <td class="px-6 py-2">
              {{ patient.Age }}
              {{ patient.AgeType == 1 ? "Years(s)" : "Month(s)" }}
            </td>
            <td class="px-6 py-2">{{ patient.DateEntryMade }}</td>
            <td>
              <div class="flex px-6 space-x-2 py-2">
                <button
                  @click="handleShowDetails(patient.PatientId)"
                  class="bg-custom-yellow text-white px-4 py-1 rounded"
                >
                  View Medical History
                </button>
                <button
                  @click="handleViewPatient(patient.PatientId)"
                  class="bg-custom-blue text-white px-4 py-1 rounded"
                >
                  View Patient
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    <PaginationComponent
      v-model:currentPage="currentPage"
      v-model:itemsPerPage="itemsPerPage"
      :totalPages="totalPages"
      :totalItems="totalItems"
      @update:currentPage="goToPage"
      @update:changePageSize="changePageSize"
    />

    <CreatePatientVisitModal
      :show="showVisitModal"
      :formData="patienRegForm"
      :patient="newVisitPatient"
    />
  </div>
</template>

<script>
// import axios from 'axios';
import apiClient from "@/services/api";
import dayjs from "dayjs";
import CreatePatientVisitModal from "../modals/CreatePatientVisit.Modal.vue";
import PaginationComponent from "@/components/shared/pagination.vue";

export default {
  name: "PatientsListTab",
  components: {
    CreatePatientVisitModal,
    PaginationComponent,
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      patients: [],
      selectedvisitId: null,
      showVisitModal: false,
      isLoading: false,
      newVisitPatient: null,
      currentPage: 1,
      itemsPerPage: 10,
      totalItems: 0, // New: Total items from backend
      totalPages: 0, // New: Total pages from backend
    };
  },
  mounted() {
    this.fetchPatientData();
  },
  methods: {
    handleViewPatient(patientId) {
      const userType = JSON.parse(localStorage.getItem('user'))?.UserType

      if (userType === 'Doctor') {
        this.$router.push({ name: 'ConsultationPatientInformationPage', params: { patientId } })
      } else if (userType === 'Nurse') {
        this.$router.push({ name: 'TriagePatientInformationPage', params: { patientId } })
      } else {
        this.$router.push({ name: 'PatientInformationPage', params: { patientId } })
      }
    },
    goToPage(pageNumber) {
      console.log("goToPage", pageNumber);
      this.currentPage = pageNumber;
      this.fetchPatientData();
    },
    changePageSize(pageSize) {
      console.log("change page size", pageSize);
      this.itemsPerPage = pageSize;
      this.fetchPatientData();
    },
    async fetchPatientData() {
      try {
        this.isLoading = true;
        const response = await apiClient.getPatientList({
          page: this.currentPage,
          per_page: this.itemsPerPage,
        });

        this.isLoading = false;
        if (response.success) {
          this.patients = response.data;
          this.totalItems = response.meta.total_items; // Assuming meta data from backend
          this.totalPages = response.meta.total_pages; // Assuming meta data from backend
          this.itemsPerPage = response.meta.per_page;
        } else {
          this.$toast.error(
            "Unable to fetch patients, please check your internet connection."
          );
        }
      } catch (error) {
        this.$toast.error(
          "Unable to fetch patients, please check your internet connection."
        );
      }
    },
    async searchPatientData(params) {
      try {
        this.isLoading = true;
        const response = await apiClient.searchPatients(params);

        if (response.success) {
          this.patients = [];
          this.patients = response.data;
        }

        this.isLoading = false;
      } catch (error) {
        this.$toast.error(
          "Unable to fetch patients file, please check your internet connection."
        );
      }
    },
    handleShowDetails(patientId) {
      const userType = JSON.parse(localStorage.getItem('user'))?.UserType

      if (userType === 'Doctor') {
        this.$router.push({ name: 'ConsultationMedicalHistoryPage', params: { patient_id: patientId } })
      } else if (userType === 'Nurse') {
        this.$router.push({ name: 'TriageMedicalHistoryPage', params: { patient_id: patientId } })
      } else {
        this.$router.push({ name: 'PatientInformationPage', params: { patientId } })
      }
    },
    formattedDate(originalDate) {
      console.log(originalDate);
      const parsedDate = dayjs(originalDate, "DD/MM/YYYY HH:mm:ss");
      console.log("parsedDate", parsedDate); // Parse custom format
      return parsedDate.format("dddd, MMMM D, YYYY h:mm:ss A"); // Format as human-readable
    },
    handleVisitCreation(patientId) {
      this.showVisitModal = true;
      this.newVisitPatient = patientId;
      console.log(patientId);
    },
  },
};
</script>

<style lang="css" scoped></style>
